import React, { useContext, useEffect, useRef, useState } from "react";
import { Button, Dropdown, Form, OverlayTrigger, Spinner } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { BiExport, BiDownload } from "react-icons/bi";
import { BsDownload, BsFileEarmarkZip, BsFileEarmarkSpreadsheet, BsFilter, BsMegaphone } from "react-icons/bs";
import { CgSortAz } from "react-icons/cg";
import { toast } from "react-toastify";
import { LoaderContext } from "../../../App";
import { DATA_VIEW_IMPORT_POLLING_INTERVAL } from "../../../core/constants/common";
import KPIService from "../../../core/service/KPIService";
import './DataView.scss';
import { FaChevronDown } from "react-icons/fa";
import Utils from "../../../core/utilities/Utils";
import { AiOutlineApartment } from "react-icons/ai";
import { useLocation, useNavigate } from "react-router-dom";

let importStatusPoller;

const DataViewHeaderActions = ({
	t,
	filter,
	regulationId,
	view,
	viewType,
	onFilterClick,
	onViewChange,
	onViewTypeChange,
	goalId
}) => {

	const ref = useRef();
	const { setLoaderDisplay } = useContext(LoaderContext);
	const [importInProgress, setImportInProgress] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();

	useEffect(() => {
		fetchImportStatus(true);
		return () => {
			// On unmounting
			clearImportStatusPoller();
		};
	}, []);

	const onExport = () => {
		setLoaderDisplay(true);
		KPIService.exportMetricDataview(regulationId, filter.reportingPeriod, filter.businessUnitIds, filter.topicIds, filter.categoryType)
			.then((response) => {
				setLoaderDisplay(false);
				let blob = new Blob([response.data], { type: 'application/octet-stream' });
				const fileURL = window.URL.createObjectURL(blob);
				let link = document.createElement('a');
				link.href = fileURL;
				link.download = `DataView-Template.xlsx`;
				link.click();
				link.remove();
			})
			.catch((error) => {
				setLoaderDisplay(false);
				toast.error(error.message, {
					position: toast.POSITION.TOP_RIGHT,
				});
			});
	};

	const onFileSelect = (e) => {
		e.preventDefault();
		const fileUploaded = e.target.files[0];
		importFile(fileUploaded);
	};

	const importFile = (file) => {
		const formData = new FormData();
		formData.append("file", file);
		setLoaderDisplay(true);
		KPIService.uploadFile(formData, regulationId, filter.reportingPeriod)
			.then((response) => {
				toast.success(response.data, {
					position: toast.POSITION.TOP_RIGHT,
				});
				setImportInProgress(true);
				fetchImportStatus(true);
			})
			.catch((error) => {
				setLoaderDisplay(false);
			})
			.finally(() => {
				setLoaderDisplay(false);
			});
	};

	const fetchImportStatus = (startPoller = false) => {
		KPIService.getImportStatus().then(res => {
			if (res.data.data) {
				setImportInProgress(true);
				if (!startPoller) return;

				startImportStatusPoller();
			} else {
				clearImportStatusPoller();
			}
		}).catch(err => {
			clearImportStatusPoller();
		});
	};


	const startImportStatusPoller = () => {
		importStatusPoller = setTimeout(function request() {
			fetchImportStatus();
			importStatusPoller = setTimeout(request, DATA_VIEW_IMPORT_POLLING_INTERVAL);
		}, DATA_VIEW_IMPORT_POLLING_INTERVAL);
	};

	const clearImportStatusPoller = () => {
		setImportInProgress(false);
		if (!importStatusPoller) return;

		clearTimeout(importStatusPoller);
	};

	const [isActive, setIsActive] = useState(false);

	const handleClick = () => {
		setIsActive(!isActive); // Toggle the active state
		onFilterClick(); // Call the provided function
	};

	const renderViewToggle = () => {
		return (
			<div className="ToggleBtn">
				<Form.Check
					className="toggle-switch"
					inline
					label={<AiOutlineApartment />}
					name="assessType"
					type="radio"
					id="inline-radio-2"
					value={'DATA_VIEW'}
					checked={view === 'DATA_VIEW' ? "checked" : ""}
					onChange={(e) => {
						onViewChange('DATA_VIEW');
					}}
				/>
				{
					viewType === 'KPI'
						?
						<Form.Check
							className="toggle-switch"
							inline
							label={<BsMegaphone />}
							name="assessType"
							type="radio"
							id="inline-radio-1"
							value={'ACTIONS_VIEW'}
							checked={view === 'ACTIONS_VIEW' ? "checked" : ""}
							onChange={(e) => {
								onViewChange('ACTIONS_VIEW');
							}}
						/>
						:
						<>
							<Form.Check
								className="toggle-switch"
								inline
								label={t("Consolidated.label")}
								name="assessType"
								type="radio"
								id="inline-radio-1"
								value={'CONSOLIDATED_VIEW'}
								checked={view === 'CONSOLIDATED_VIEW' ? "checked" : ""}
								onChange={(e) => {
									onViewChange('CONSOLIDATED_VIEW');
								}}
							/>
							<Form.Check
								className="toggle-switch"
								inline
								label={t("Comparison.label")}
								name="assessType"
								type="radio"
								id="inline-radio-7"
								value={'COMPARE'}
								checked={view === 'COMPARE' ? "checked" : ""}
								onChange={(e) => {
									onViewChange('COMPARE');
								}}
							/>
						</>
				}
			</div>
		);
	};

	const renderViewTypeToggle = () => {
		return (
			<div className="mx-2">
				<Form.Check
					className="toggle-switch"
					inline
					label={t("ESG.label")}
					type="radio"
					id="inline-radio-3"
					value={'ESG_REPORTING'}
					checked={viewType === 'ESG_REPORTING' ? "checked" : ""}
					onChange={(e) => {
						onViewTypeChange('ESG_REPORTING');
					}}
				/>
				<Form.Check
					className="toggle-switch"
					inline
					label={t("GoalsandKPIs.label")}
					type="radio"
					id="inline-radio-4"
					value={'KPI'}
					checked={viewType === 'KPI' ? "checked" : ""}
					onChange={(e) => {
						onViewTypeChange('KPI');
					}}
				/>
			</div>
		);
	};

	const onDownloadDocuments = () => {
		const date = new Date(filter.reportingPeriod);
		const month = (date.getMonth() + 1).toString().padStart(2, '0');
		const year = date.getFullYear();
		let categoryType = '';
		if (filter.categoryType === 0) {
			categoryType = 'ENVIRONMENTAL';
		} else if (filter.categoryType === 1) {
			categoryType = 'SOCIAL';
		} else if (filter.categoryType === 2) {
			categoryType = 'GOVERNANCE';
		}
		setLoaderDisplay(true);
		KPIService.exportMetricDataAttachments(regulationId, filter.reportingPeriod, filter.businessUnitIds, filter.topicIds, categoryType)
			.then((response) => {
				setLoaderDisplay(false);
				let blob = new Blob([response.data], { type: 'application/octet-stream' });
				const fileURL = window.URL.createObjectURL(blob);
				let link = document.createElement('a');
				link.href = fileURL;
				link.download = `attachments-${month}-${year}.zip`;
				link.click();
				link.remove();
			})
			.catch(async (error) => {
				setLoaderDisplay(false);
				const blobToText = await error.response.data.text();
				const textToJson = JSON.parse(blobToText);
				toast.error(textToJson.message, {
					position: toast.POSITION.TOP_RIGHT,
				});
			});
	};

	const renderImportButton = () => {
		return (
			<div>
				<input hidden
					ref={ref}
					type="file"
					id="Browser_input"
					onChange={(e) => onFileSelect(e)}
					onClick={(e) => { e.target.value = null; }}
				/>
				<OverlayTrigger
					placement="top"
					overlay={Utils.renderTooltip(
						t("Import.label")
					)}>
					<Button variant="headbtn" onClick={() => ref.current.click()}
						className="btn"
						disabled={importInProgress}
						title={t("FileformatText.label")}>
						{
							importInProgress
								?
								<>
									<Spinner
										as="span"
										animation="border"
										size="sm"
										role="status"
										aria-hidden="true"
									/>
									<span className="px-1">{t('ImportInProgress.label')}</span>
								</>
								:
								<>
									<BsDownload />
								</>
						}
					</Button>
				</OverlayTrigger>
			</div >
		);
	};

	const renderNavigateViewButtons = () => {
		return (
			<div className="ToggleBtn">
				<Form.Check
					className="toggle-switch"
					inline
					label="Status"
					type="radio"
					id="statusView"
					value={'Status'}
					checked={''}
					onChange={(e) => toggleViews(e.target.id)}
				/>
				<Form.Check
					className="toggle-switch"
					inline
					label="Analytical"
					type="radio"
					id="analyticalView"
					value={'Analytical'}
					checked={''}
					onChange={(e) => toggleViews(e.target.id)}
				/>
				<Form.Check
					className="toggle-switch"
					inline
					label="Data"
					type="radio"
					id="dataView"
					value={'Data'}
					checked={'checked'}
				/>
			</div>
		);
	};

	const toggleViews = (view) => {
		if (view === 'statusView') {
			navigate(`/company_admin/kpistatusview/goal/${goalId}`, { state: { goal: location.state.goal } });
		} else if (view === 'analyticalView') {
			navigate(`/company_admin/dashboard/goal/${goalId}`, { state: { goal: location.state.goal } });
		}
	}

	return (
		<div className="header-actions">
			{viewType === 'KPI' && renderNavigateViewButtons()}
			{renderViewToggle()}
			<OverlayTrigger
				placement="top"
				overlay={Utils.renderTooltip(t("Filter.label"))}
			>
				<button
					className={`btn btn-headbtn ${isActive ? 'active' : ''}`}
					onClick={handleClick}>
					<BsFilter />
				</button>
			</OverlayTrigger>

			{
				view !== 'COMPARE' && view !== 'DATA_VIEW' &&
				<>
					<OverlayTrigger
						placement="top"
						overlay={Utils.renderTooltip(
							t("ExportExcel.label")
						)}>
						<Button variant="headbtn"
							onClick={() => onExport()}
						>
							<BiExport />
						</Button>
					</OverlayTrigger>
					{renderImportButton()}
				</>
			}
			{
				view !== 'COMPARE' && view === 'DATA_VIEW' &&
				<Dropdown>
					<Dropdown.Toggle variant="headbtn" id="dropdown-basic" className={`sbmBtn ${viewType === 'KPI' ? 'disabled' : ''}`} disabled={viewType === 'KPI'}>
						<BiDownload />
					</Dropdown.Toggle>

					<Dropdown.Menu>
						<OverlayTrigger
							placement="top"
							overlay={Utils.renderTooltip(
								t("DownloadDocuments.label")
							)}>
							<Dropdown.Item onClick={() => onDownloadDocuments()}>
								<BsFileEarmarkZip className="me-1" /> {t("Documents&File.label")}
							</Dropdown.Item>
						</OverlayTrigger>
						<OverlayTrigger
							placement="top"
							overlay={Utils.renderTooltip(
								t("DownloadExcel.label")
							)}>
							<Dropdown.Item onClick={() => onExport()}>
								<BsFileEarmarkSpreadsheet className="me-1" /> {t("Spreadsheet.label")}
							</Dropdown.Item>
						</OverlayTrigger>
					</Dropdown.Menu>
				</Dropdown>
			}
		</div>
	);
};

export default withTranslation()(DataViewHeaderActions);